/* global HTMLElement, HTMLDivElement */
// use-drag-scroll — click-and-drag horizontal panning for overflow-x rows.
// The chip filter rows hide their scrollbar (no-scrollbar) for a clean look,
// which leaves plain-mouse desktop users unable to scroll. This hook adds
// grab-to-pan with the mouse while leaving native touch scrolling untouched,
// and suppresses the click that would otherwise fire on a child after a drag.
import { useRef } from "react";
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from "react";

export function useDragScroll<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const state = useRef({ down: false, startX: 0, startLeft: 0, moved: false });

  return {
    ref,
    dragProps: {
      onPointerDown(e: ReactPointerEvent<T>) {
        // Touch already scrolls natively; only enhance mouse/pen.
        if (e.pointerType === "touch") return;
        const el = ref.current;
        if (!el) return;
        state.current = { down: true, startX: e.clientX, startLeft: el.scrollLeft, moved: false };
      },
      onPointerMove(e: ReactPointerEvent<T>) {
        const el = ref.current;
        if (!el || !state.current.down) return;
        const dx = e.clientX - state.current.startX;
        if (Math.abs(dx) > 4) state.current.moved = true;
        el.scrollLeft = state.current.startLeft - dx;
      },
      onPointerUp() {
        state.current.down = false;
      },
      onPointerLeave() {
        state.current.down = false;
      },
      // Capture-phase: swallow the click that ends a drag so a chip isn't toggled.
      onClickCapture(e: ReactMouseEvent<T>) {
        if (state.current.moved) {
          e.preventDefault();
          e.stopPropagation();
          state.current.moved = false;
        }
      },
    },
  };
}
